import { ERC20_ABI } from '@trinity/pharaoh-common';
import { Contract } from 'ethers';
export class TokenService {
    constructor(runner, tokenAddresses) {
        this.tokenContracts = new Map();
        this.tokenDecimals = new Map();
        this.tokenSymbols = new Map();
        this.initialBalances = new Map(); // Store initial balances for tokens
        this.runner = runner;
        this.tokenAddresses = tokenAddresses;
    }
    /**
     * Get token contract, create if it doesn't exist
     */
    async getTokenContract(tokenAddressOrSymbol) {
        // Check if input is a symbol
        let tokenAddress = tokenAddressOrSymbol;
        if (this.tokenAddresses[tokenAddressOrSymbol]) {
            tokenAddress = this.tokenAddresses[tokenAddressOrSymbol];
        }
        // Return cached contract if exists
        if (this.tokenContracts.has(tokenAddress)) {
            return this.tokenContracts.get(tokenAddress);
        }
        // Create new contract
        const contract = new Contract(tokenAddress, ERC20_ABI, this.runner);
        this.tokenContracts.set(tokenAddress, contract);
        return contract;
    }
    /**
     * Get token decimals
     */
    async getTokenDecimals(tokenAddressOrSymbol) {
        // Check if input is a symbol
        let tokenAddress = tokenAddressOrSymbol;
        if (this.tokenAddresses[tokenAddressOrSymbol]) {
            tokenAddress = this.tokenAddresses[tokenAddressOrSymbol];
        }
        // Return cached decimals if exists
        if (this.tokenDecimals.has(tokenAddress)) {
            return this.tokenDecimals.get(tokenAddress);
        }
        // Get decimals from contract
        const contract = await this.getTokenContract(tokenAddress);
        const decimals = await contract.decimals();
        this.tokenDecimals.set(tokenAddress, decimals);
        return decimals;
    }
    /**
     * Get token symbol
     */
    async getTokenSymbol(tokenAddress) {
        // Return cached symbol if exists
        if (this.tokenSymbols.has(tokenAddress)) {
            return this.tokenSymbols.get(tokenAddress);
        }
        // Check if we have an inverted mapping
        for (const [symbol, address] of Object.entries(this.tokenAddresses)) {
            if (address.toLowerCase() === tokenAddress.toLowerCase()) {
                this.tokenSymbols.set(tokenAddress, symbol);
                return symbol;
            }
        }
        // If not found, get from contract
        const contract = await this.getTokenContract(tokenAddress);
        const symbol = await contract.symbol();
        this.tokenSymbols.set(tokenAddress, symbol);
        return symbol;
    }
    /**
     * Get token balance
     */
    async getBalance(tokenAddressOrSymbol, owner) {
        // Check if this is a native token (ETH/AVAX)
        const isNative = tokenAddressOrSymbol.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
            tokenAddressOrSymbol.toLowerCase() === '0x0000000000000000000000000000000000000000' ||
            tokenAddressOrSymbol.toLowerCase() === 'avax';
        if (isNative) {
            // For native tokens, use the provider to get the balance
            let provider;
            if ('getBalance' in this.runner) {
                // Runner is already a provider
                provider = this.runner;
            }
            else if ('provider' in this.runner && this.runner.provider) {
                // Runner is a signer with a provider
                provider = this.runner.provider;
            }
            else {
                throw new Error('Provider not available for getting native token balance');
            }
            const balance = await provider.getBalance(owner);
            // Native tokens have 18 decimals
            return {
                balance,
                decimals: 18,
            };
        }
        // Original code for ERC20 tokens
        const contract = await this.getTokenContract(tokenAddressOrSymbol);
        const decimals = await this.getTokenDecimals(tokenAddressOrSymbol);
        const balance = await contract.balanceOf(owner);
        return {
            balance,
            decimals,
        };
    }
    /**
     * Approve token for spending
     */
    async approve(tokenAddressOrSymbol, spender, amount, signer) {
        const provider = signer.provider;
        if (!provider) {
            throw new Error('Provider not available');
        }
        // Get the current nonce for the signer
        const nonce = await provider.getTransactionCount(await signer.getAddress());
        const contract = await this.getTokenContract(tokenAddressOrSymbol);
        const contractConnected = contract.connect(signer);
        return contractConnected.approve(spender, amount, {
            nonce,
        });
    }
    /**
     * Check token allowance
     */
    async getAllowance(tokenAddressOrSymbol, spender, owner) {
        const contract = await this.getTokenContract(tokenAddressOrSymbol);
        return contract.allowance(owner, spender);
    }
    /**
     * Parse token pair to addresses
     */
    parseTokenPair(pairString) {
        const [token0Symbol, token1Symbol] = pairString.split('-');
        if (!token0Symbol || !token1Symbol) {
            throw new Error("Invalid pair format. Use format like 'USDC-USDt'");
        }
        if (!this.tokenAddresses[token0Symbol] || !this.tokenAddresses[token1Symbol]) {
            throw new Error(`Address not found for ${token0Symbol} or ${token1Symbol}`);
        }
        const fromToken = this.tokenAddresses[token0Symbol];
        const toToken = this.tokenAddresses[token1Symbol];
        return {
            fromToken,
            toToken,
            fromSymbol: token0Symbol,
            toSymbol: token1Symbol,
        };
    }
    /**
     * Get initial balance for a token or store it
     * @param tokenAddress The token address
     * @param owner The owner's address
     * @returns The stored initial balance or undefined if not found
     */
    async getInitialBalanceFor(tokenAddress, owner) {
        const key = `${tokenAddress.toLowerCase()}-${owner.toLowerCase()}`;
        // If we don't have an initial balance stored, get it and store it
        if (!this.initialBalances.has(key)) {
            try {
                const contract = await this.getTokenContract(tokenAddress);
                const balance = await contract.balanceOf(owner);
                this.initialBalances.set(key, balance);
                return balance;
            }
            catch (error) {
                console.error('Error getting initial balance:', error);
                return undefined;
            }
        }
        return this.initialBalances.get(key);
    }
    /**
     * Sort tokens according to Uniswap V3 convention
     */
    sortTokens(tokenAAddress, tokenBAddress) {
        // Sort tokens (lower address first according to Uniswap V3 convention)
        return tokenAAddress.toLowerCase() < tokenBAddress.toLowerCase()
            ? [tokenAAddress, tokenBAddress]
            : [tokenBAddress, tokenAAddress];
    }
    /**
     * Get addresses of wrapped native tokens (WETH, WAVAX)
     * @returns Array of wrapped native token addresses
     */
    getNativeWrappedTokenAddresses() {
        const nativeWrappedTokens = [];
        // Look for common wrapped native tokens in our address book
        const wrappedSymbols = ['WETH', 'WAVAX', 'WMATIC', 'WBNB'];
        for (const symbol of wrappedSymbols) {
            if (this.tokenAddresses[symbol]) {
                nativeWrappedTokens.push(this.tokenAddresses[symbol]);
            }
        }
        return nativeWrappedTokens;
    }
}
